import type { Plan } from "@viamissa/planner";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { ChurchList } from "./components/ChurchList";
import { Itinerary } from "./components/Itinerary";
import { draftFromLink, timeChoice, TripEditor, type Draft } from "./components/TripEditor";
import { KM_PER_MILE } from "./lib/format";
import { readLink } from "./lib/link";
import { resolvePlaces, runTrip, type TripResult } from "./lib/trip";

// MapLibre is most of the bundle; load it only once there is a route to show.
const RouteMap = lazy(() => import("./components/RouteMap").then((module) => ({ default: module.RouteMap })));

type Mode = "itinerary" | "churches";

function initialLink(): string {
  try {
    return new URLSearchParams(window.location.search).get("link") ?? "";
  } catch {
    return "";
  }
}

export function App() {
  const [linkText, setLinkText] = useState(initialLink);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ step: string; fraction?: number } | null>(null);
  const [result, setResult] = useState<TripResult | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [mode, setMode] = useState<Mode>("itinerary");
  const abort = useRef<AbortController | null>(null);

  const read = useCallback(async (text: string) => {
    setError(null);
    setResult(null);
    if (!text.trim()) return;
    setProgress({ step: "Reading the link" });
    try {
      const parsed = await readLink(text);
      if (!parsed.ok) {
        setError(parsed.error);
        return;
      }
      if (parsed.link.travelMode && parsed.link.travelMode !== "driving") {
        setError("That link is for walking, cycling or transit directions. Viamissa plans drives, so it will route by car.");
      }
      setDraft(draftFromLink(parsed.link));
      const url = new URL(window.location.href);
      url.searchParams.set("link", text.trim());
      window.history.replaceState(null, "", url);
    } finally {
      setProgress(null);
    }
  }, []);

  useEffect(() => {
    if (linkText) void read(linkText);
    // Only on first load: a shared ?link= opens straight into the editor.
  }, []);

  const plan_ = async (nextMode: Mode) => {
    if (!draft) return;
    const time = timeChoice(draft);
    if (!time) {
      setError("Pick a date and time.");
      return;
    }
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setError(null);
    setMode(nextMode);
    setResult(null);
    try {
      setProgress({ step: "Finding the places" });
      const places = await resolvePlaces(draft.stops);
      const next = await runTrip(
        {
          places,
          via: draft.via,
          avoid: draft.avoid,
          time,
          masses: draft.masses,
          corridorKm: Math.max(1, draft.corridorMi) * KM_PER_MILE,
          maxDetourMin: draft.maxDetourMin,
          marginMin: draft.marginMin,
          drivingHours: draft.limitHours ? { startHour: draft.startHour, endHour: draft.endHour } : null,
        },
        (step, fraction) => setProgress({ step, fraction }),
        controller.signal,
      );
      setResult(next);
      setPlan(next.plan);
    } catch (caught) {
      if ((caught as Error).name === "AbortError") return;
      setError((caught as Error).message || "Something went wrong. Try again in a moment.");
    } finally {
      if (abort.current === controller) setProgress(null);
    }
  };

  const planned = plan?.stops.map((stop) => stop.candidate.churchId) ?? [];
  const select = useCallback((churchId: string) => {
    setMode("churches");
    requestAnimationFrame(() => document.getElementById(`church-${churchId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }));
  }, []);

  return (
    <div className="page">
      <header className="masthead">
        <h1>Viamissa</h1>
        <p>Mass on the way. Paste a Google Maps route and find churches and Mass times along it.</p>
      </header>

      <form
        className="link-form"
        onSubmit={(event) => {
          event.preventDefault();
          void read(linkText);
        }}
      >
        <label htmlFor="link">Google Maps directions link</label>
        <div className="row">
          <input
            id="link"
            inputMode="url"
            placeholder="https://maps.app.goo.gl/…"
            value={linkText}
            onChange={(event) => setLinkText(event.target.value)}
          />
          <button type="submit" disabled={!linkText.trim() || !!progress}>
            Read link
          </button>
        </div>
        {!draft && (
          <button type="button" className="link" onClick={() => setDraft(draftFromLink(null))}>
            Or type the places instead
          </button>
        )}
      </form>

      {error && <p className="error" role="alert">{error}</p>}

      {draft && <TripEditor draft={draft} onChange={setDraft} onSubmit={(m) => void plan_(m)} busy={!!progress} />}

      {progress && (
        <div className="progress" role="status" aria-live="polite">
          <p>{progress.step}…</p>
          <progress value={progress.fraction ?? undefined} max={1} />
        </div>
      )}

      {result && plan && (
        <main className="results">
          <Suspense fallback={<div className="map" />}>
            <RouteMap result={result} planned={mode === "itinerary" ? planned : []} onSelect={select} />
          </Suspense>
          <div className="tabs" role="tablist">
            <button role="tab" aria-selected={mode === "itinerary"} onClick={() => setMode("itinerary")}>
              Itinerary
            </button>
            <button role="tab" aria-selected={mode === "churches"} onClick={() => setMode("churches")}>
              Churches along the way ({result.churches.length})
            </button>
          </div>
          {mode === "itinerary" ? <Itinerary result={result} plan={plan} onChoose={setPlan} /> : <ChurchList result={result} />}
        </main>
      )}

      <footer className="credits">
        <p>
          Routing by Valhalla and OSRM, geocoding by Photon, maps by OpenFreeMap, all on © OpenStreetMap contributors.
        </p>
      </footer>
    </div>
  );
}
